import React from "react";
import { useTranslation } from "react-i18next";
import { Field, FieldInputProps } from "react-final-form";

// Primereact
import {
	InputNumber,
	InputNumberChangeEvent,
	InputNumberValueChangeEvent,
} from "primereact/inputnumber";
import { ObjectUtils, classNames } from "primereact/utils";

// Own "library"
import UiFieldLabel from "../field-label/FieldLabel.component";
import UiFieldErrors from "../field-errors/FieldErrors.component";
import {
	UiInputNumberProps,
	UiInputNumberValueChangeEvent,
} from "../../models/components/input-number.model";
import { Validations } from "../../services/form/validations.service";
import { FormService } from "../../services/form/form.service";

const UiInputNumber = (props: UiInputNumberProps) => {
	const { t } = useTranslation("common");

	/**
	 * Remove from the props the ones that need special treatment
	 * (like the placholder that needs to be translated, etc.).
	 */
	const otherProps = ObjectUtils.findDiffKeys(props, {
		leftIcon: props.leftIcon,
		rightIcon: props.rightIcon,
		placeholder: props?.placeholder,
		onChange: props.onChange,
		onBlur: props.onBlur,
		name: props.name,
		value: props.value,
		leftIconFn: props.leftIconFn,
		rightIconFn: props.rightIconFn,
		className: props.className,
		maxDigits: props.maxDigits,
		removeBottomSpacer: props.removeBottomSpacer,
	});

	const handleValueChange = (
		event: InputNumberValueChangeEvent,
		input: FieldInputProps<number, HTMLElement>
	): void => {
		// PrimeReact v10 widened the change value to include undefined; normalize it
		// to number | null so it matches our form fields.
		let value: number | null = event.value ?? null;

		// Format number if necessary
		if (props.maxDigits) {
			value = FormService.maxDigits(value, props.maxDigits, props.maxFractionDigits ?? 0);
		}

		const normalizedEvent: UiInputNumberValueChangeEvent = { ...event, value };

		if (input) {
			input.onChange(normalizedEvent);
		}

		if (props.onValueChange) props.onValueChange(normalizedEvent);
	};

	const handleChange = (event: InputNumberChangeEvent) => {
		// Format number if necessary
		if (props.maxDigits) {
			event.value = FormService.maxDigits(
				event.value,
				props.maxDigits,
				props.maxFractionDigits ?? 0
			);
		}

		if (props.onChange) props.onChange(event);
	};

	const handleBlur = (
		event: React.FocusEvent<HTMLInputElement, Element>,
		input: FieldInputProps<number, HTMLElement>
	): void => {
		if (input) input.onBlur();
		// Execute onBlur callback function
		if (props.onBlur) props.onBlur(event);
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
		if (props.onKeyDown) props.onKeyDown(event);
	};

	return (
		<Field
			name={props.name}
			validate={Validations.composeValidators(props.validations)}
			render={({ input, meta }) => (
				<div
					className={classNames("k-input-container", props.removeBottomSpacer && "mb-0")}
				>
					<div className="k-inputnumber-container">
						<UiFieldLabel
							id={props.id}
							label={props.label}
							disabled={props.disabled}
							leftIcon={props.leftIcon}
							rightIcon={props.rightIcon}
							meta={meta}
							validations={props.validations}
						>
							{/* Left icon */}
							{props.leftIcon && (
								<i
									className={classNames(
										"input-icon",
										props.leftIcon,
										props.leftIconFn ? "is-clickable" : ""
									)}
									onClick={props.leftIconFn}
									onKeyDown={(e) => {
										if (e.key === "Enter" || e.key === " ") {
											e.preventDefault();
											props.leftIconFn?.();
										}
									}}
									role="button"
									tabIndex={0}
								/>
							)}

							{/* Right icon */}
							{props.rightIcon && (
								<i
									className={classNames(
										"input-icon",
										props.rightIcon,
										props.rightIconFn ? "is-clickable" : ""
									)}
									onClick={props.rightIconFn}
									onKeyDown={(e) => {
										if (e.key === "Enter" || e.key === " ") {
											e.preventDefault();
											props.rightIconFn?.();
										}
									}}
									role="button"
									tabIndex={0}
								/>
							)}

							{/* Input */}
							<InputNumber
								placeholder={props?.placeholder ? t(props.placeholder) : undefined}
								className={classNames(
									{ "p-invalid": !!(meta.touched && meta.error) },
									props.className
								)}
								name={input.name}
								value={input.value || input.value === 0 ? input.value : null} // " || null" added to fix default value = 0 when initialized as null
								onValueChange={(event) => handleValueChange(event, input)}
								onChange={(event) => handleChange(event)}
								onBlur={(event) => handleBlur(event, input)}
								onKeyDown={(event) => handleKeyDown(event)}
								data-testid={props.testId}
								{...otherProps}
							/>
						</UiFieldLabel>
					</div>

					{/* Validation messages */}
					<UiFieldErrors {...meta}></UiFieldErrors>
				</div>
			)}
		/>
	);
};

export default UiInputNumber;
